import React, { useState } from 'react';
import { Row, Col, Alert } from 'react-bootstrap';
import Container from '../components/Container';
import SectionHeader from '../components/SectionHeader';
import Button from '../components/Button';
import { Upload, Users } from 'lucide-react';
import '../styles/components.css';

const MAX_WORDS = 400;

const NousRejoindre = () => {
  const [formData, setFormData] = useState({
    nom: '',
    email: '',
    message: '',
    cv: null
  });

  const [errors, setErrors] = useState({});
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null
  const [isSubmitting, setIsSubmitting] = useState(false);

  const apiUrl =
    process.env.REACT_APP_API_URL ||
    (process.env.NODE_ENV === 'development' ? 'http://localhost:5000' : '');

  const countWords = (text) => {
    return text
      .trim()
      .split(/\s+/)
      .filter(Boolean).length;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'message') {
      const words = countWords(value);
      if (words <= MAX_WORDS) {
        setFormData((prev) => ({ ...prev, [name]: value }));
      } else {
        setFormData((prev) => ({ ...prev, [name]: value }));
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setFormData((prev) => ({ ...prev, cv: null }));
      return;
    }

    if (file.type !== 'application/pdf') {
      setErrors((prev) => ({
        ...prev,
        cv: 'Le CV doit être au format PDF.'
      }));
      setFormData((prev) => ({ ...prev, cv: null }));
      return;
    }

    setFormData((prev) => ({ ...prev, cv: file }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next.cv;
      return next;
    });
  };

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.nom.trim()) {
      newErrors.nom = 'Le nom est requis';
    }

    if (!formData.email.trim()) {
      newErrors.email = "L'email est requis";
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Format d’email invalide';
    }

    const wordCount = countWords(formData.message);
    if (!formData.message.trim()) {
      newErrors.message = 'Le message est requis';
    } else if (wordCount > MAX_WORDS) {
      newErrors.message = `Le message ne doit pas dépasser ${MAX_WORDS} mots (actuellement ${wordCount}).`;
    }

    if (!formData.cv) {
      newErrors.cv = 'Merci de joindre votre CV au format PDF.';
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus(null);

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      if (!apiUrl) {
        console.warn(
          'NousRejoindre: REACT_APP_API_URL non défini. Définir dans .env puis rebuild.'
        );
        // Fallback démo : on simule un envoi réussi
        await new Promise((resolve) => setTimeout(resolve, 1500));
      } else {
        const formDataToSend = new FormData();
        formDataToSend.append('nom', formData.nom);
        formDataToSend.append('email', formData.email);
        formDataToSend.append('message', formData.message);
        if (formData.cv) {
          formDataToSend.append('cv', formData.cv);
        }

        const response = await fetch(
          `${apiUrl.replace(/\/$/, '')}/api/candidatures`,
          {
            method: 'POST',
            body: formDataToSend
          }
        );

        const data = await response.json().catch(() => ({}));
        if (!response.ok || !data.success) {
          throw new Error(data.error || `Erreur ${response.status}`);
        }
      }

      setSubmitStatus('success');
      setFormData({
        nom: '',
        email: '',
        message: '',
        cv: null
      });
      setErrors({});
    } catch (err) {
      console.error('Erreur lors de l’envoi de la candidature :', err);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const currentWordCount = countWords(formData.message || '');

  return (
    <div className="section" style={{ overflow: 'hidden' }}>
      <Container>
        <Row className="g-4 align-items-start">
          <Col lg={7}>
            <div className="card-modern" style={{ marginBottom: 'var(--spacing-2xl)' }}>
              <SectionHeader
                title="Nous rejoindre"
                subtitle="Envie de rejoindre l’équipe Moov4Goods ? Envoyez-nous votre candidature."
              />

              {submitStatus === 'success' && (
                <Alert variant="success" className="mb-4">
                  Votre candidature a bien été enregistrée. Nous reviendrons vers vous dès que possible.
                </Alert>
              )}

              {submitStatus === 'error' && (
                <Alert variant="danger" className="mb-4">
                  Une erreur est survenue lors de l’envoi. Veuillez réessayer plus tard.
                </Alert>
              )}

              <form onSubmit={handleSubmit}>
                <div className="form-group-modern">
                  <label htmlFor="nom" className="form-label-modern">
                    Nom complet <span style={{ color: 'var(--color-error)' }}>*</span>
                  </label>
                  <input
                    type="text"
                    id="nom"
                    name="nom"
                    value={formData.nom}
                    onChange={handleChange}
                    className={`form-control-modern ${errors.nom ? 'is-invalid' : ''}`}
                    placeholder="Votre nom"
                    required
                  />
                  {errors.nom && <div className="form-error">{errors.nom}</div>}
                </div>

                <div className="form-group-modern">
                  <label htmlFor="email" className="form-label-modern">
                    Email <span style={{ color: 'var(--color-error)' }}>*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`form-control-modern ${errors.email ? 'is-invalid' : ''}`}
                    placeholder="votre.email@exemple.com"
                    required
                  />
                  {errors.email && <div className="form-error">{errors.email}</div>}
                </div>

                <div className="form-group-modern">
                  <label htmlFor="message" className="form-label-modern">
                    Message de motivation (max. {MAX_WORDS} mots){' '}
                    <span style={{ color: 'var(--color-error)' }}>*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    className={`form-control-modern ${errors.message ? 'is-invalid' : ''}`}
                    placeholder="Présentez-vous et expliquez pourquoi vous souhaitez rejoindre Moov4Goods..."
                    rows="6"
                    required
                  />
                  <div
                    style={{
                      marginTop: 'var(--spacing-xs)',
                      fontSize: 'var(--font-size-sm)',
                      color:
                        currentWordCount > MAX_WORDS
                          ? 'var(--color-error)'
                          : 'var(--color-text-muted)'
                    }}
                  >
                    {currentWordCount} / {MAX_WORDS} mots
                  </div>
                  {errors.message && <div className="form-error">{errors.message}</div>}
                </div>

                <div className="form-group-modern">
                  <label htmlFor="cv" className="form-label-modern">
                    CV (format PDF) <span style={{ color: 'var(--color-error)' }}>*</span>
                  </label>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--spacing-md)',
                      flexWrap: 'wrap'
                    }}
                  >
                    <label
                      htmlFor="cv"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        cursor: 'pointer'
                      }}
                    >
                      <input
                        id="cv"
                        name="cv"
                        type="file"
                        accept="application/pdf"
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                      />
                      <span className="btn-modern btn-modern-outline">
                        <Upload size={18} style={{ marginRight: '0.25rem' }} />
                        Choisir un fichier
                      </span>
                    </label>
                    <span
                      style={{
                        fontSize: 'var(--font-size-sm)',
                        color: 'var(--color-text-light)'
                      }}
                    >
                      {formData.cv ? formData.cv.name : 'Aucun fichier sélectionné'}
                    </span>
                  </div>
                  {errors.cv && <div className="form-error">{errors.cv}</div>}
                </div>

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  disabled={isSubmitting}
                  style={{ width: '100%', marginTop: 'var(--spacing-md)' }}
                >
                  {isSubmitting ? 'Envoi en cours...' : 'Envoyer ma candidature'}
                </Button>
              </form>
            </div>
          </Col>

          <Col lg={5}>
            <div>
              <div
                style={{
                  borderRadius: 'var(--border-radius-xl)',
                  overflow: 'hidden',
                  marginBottom: 'var(--spacing-xl)',
                  boxShadow: 'var(--shadow-md)'
                }}
              >
                <img
                  src="/images/equipe.jpg"
                  alt="Équipe Moov4Goods"
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
              </div>
              <div>
                <h3 style={{ marginBottom: 'var(--spacing-md)', color: 'var(--color-text)' }}>
                  Rejoindre Moov4Goods
                </h3>
                <p
                  style={{
                    fontSize: 'var(--font-size-base)',
                    color: 'var(--color-text-light)',
                    lineHeight: 'var(--line-height-relaxed)',
                    marginBottom: 'var(--spacing-lg)'
                  }}
                >
                  Nous sommes toujours à l’écoute de personnes motivées et sensibles aux enjeux
                  environnementaux pour renforcer notre équipe.
                </p>
                <p
                  style={{
                    fontSize: 'var(--font-size-base)',
                    color: 'var(--color-text-light)',
                    lineHeight: 'var(--line-height-relaxed)',
                    marginBottom: 0
                  }}
                >
                  Que vous soyez en recherche d’emploi, de stage ou de mission ponctuelle, n’hésitez pas à
                  nous envoyer votre profil. Nous étudions chaque candidature avec attention.
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default NousRejoindre;

